import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { videos } from "@/mocks/videos";
import VideoCard from "@/components/feature/VideoCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";
import { formatCount } from "@/utils/format";
import VideoPlayer from "./components/VideoPlayer";

export default function DetailPage() {
  const { videoId } = useParams();
  const video = videos.find((v) => v.id === videoId);
  const [copied, setCopied] = useState(false);

  const related = useMemo(() => {
    if (!video) return [];
    const sameCategory = videos.filter((v) => v.categoryId === video.categoryId && v.id !== video.id);
    const others = videos.filter((v) => v.categoryId !== video.categoryId && v.id !== video.id);
    return [...sameCategory, ...others].slice(0, 6);
  }, [video]);

  const hot = useMemo(() => [...videos].sort((a, b) => b.views - a.views).slice(0, 5), []);

  usePageMeta({
    title: video ? `${video.title} - 吃瓜视频` : "视频详情 - 吃瓜视频",
    description: video
      ? `${video.summary}（发布时间：${video.publishTime}，来源：${video.source}）`
      : "吃瓜视频视频详情页，观看娱乐视频并阅读相关报道。",
    keywords: video ? `${video.title},${video.categoryName},吃瓜视频` : "吃瓜视频,视频详情",
  });

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement("input");
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!video) {
    return (
      <div className="w-full px-4 py-20 md:px-8">
        <div className="mx-auto flex max-w-md flex-col items-center rounded-lg border border-background-200 bg-background-50 px-6 py-14 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-500">
            <i className="ri-file-unknow-line" />
          </span>
          <h1 className="mt-4 font-heading text-lg font-bold text-foreground-950">没有找到这个视频</h1>
          <p className="mt-2 text-sm text-foreground-600">视频可能已被移除，去看看其他精彩内容吧。</p>
          <Link
            to="/"
            className="mt-5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb
        items={[
          { label: "首页", href: "/" },
          { label: video.categoryName, href: `/category/${video.categoryId}` },
          { label: "视频详情" },
        ]}
      />

      <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-10">
        <div className="lg:col-span-2">
          <VideoPlayer video={video} />

          <h1 className="mt-6 font-heading text-xl font-black leading-snug tracking-tight text-foreground-950 md:text-3xl">
            {video.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-600 md:text-sm">
            <Link
              to={`/category/${video.categoryId}`}
              className="flex items-center gap-1 rounded-full bg-primary-50 px-3 py-1 font-medium text-primary-600 transition-colors hover:bg-primary-100"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-folder-line text-sm" />
              </span>
              {video.categoryName}
            </Link>
            <span className="flex items-center gap-1">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-calendar-line text-sm" />
              </span>
              {video.publishTime}
            </span>
            <span className="flex items-center gap-1">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-play-line text-sm" />
              </span>
              {formatCount(video.views)} 次播放
            </span>
            <span className="flex items-center gap-1">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-thumb-up-line text-sm" />
              </span>
              {formatCount(video.likes)} 点赞
            </span>
            <span className="flex items-center gap-1">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-article-line text-sm" />
              </span>
              来源：{video.source}
            </span>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            {video.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                #{tag}
              </span>
            ))}
          </div>

          <div className="relative mt-6">
            <button
              type="button"
              onClick={handleShare}
              className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-accent-300 hover:text-accent-600 whitespace-nowrap"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-share-forward-line text-base" />
              </span>
              复制链接分享
            </button>
            {copied && (
              <span className="absolute left-0 top-full mt-2 flex items-center gap-1 rounded-md bg-foreground-950 px-3 py-1.5 text-xs text-background-50 animate-fade-in">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-check-line text-sm" />
                </span>
                链接已复制到剪贴板
              </span>
            )}
          </div>

          <article className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
            <h2 className="font-heading text-lg font-bold text-foreground-950">视频简介</h2>
            <p className="mt-3 text-sm leading-7 text-foreground-700 md:text-base">{video.summary}</p>
            <div className="mt-5 space-y-4 border-t border-background-200 pt-5">
              {video.content.map((paragraph, i) => (
                <p key={i} className="text-sm leading-7 text-foreground-700 md:text-base">
                  {paragraph}
                </p>
              ))}
            </div>
            <p className="mt-6 text-xs text-foreground-500">
              本文由吃瓜视频编辑整理，内容为演示用途，如有更新请以官方发布为准。
            </p>
          </article>
        </div>

        <aside className="lg:col-span-1">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="mb-4 flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-600">
                <i className="ri-article-line" />
              </span>
              <h2 className="font-heading text-base font-bold text-foreground-950">相关推荐</h2>
            </div>
            <div className="space-y-3">
              {related.map((item) => (
                <VideoCard key={item.id} video={item} horizontal />
              ))}
            </div>
          </div>

          <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="mb-4 flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-600">
                <i className="ri-fire-line" />
              </span>
              <h2 className="font-heading text-base font-bold text-foreground-950">热门榜单</h2>
            </div>
            <ol className="space-y-3">
              {hot.map((item, i) => (
                <li key={item.id}>
                  <Link to={`/detail/${item.id}`} className="group flex items-center gap-3">
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs font-black ${
                        i < 3 ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-700"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground-800 transition-colors group-hover:text-primary-600">
                      {item.title}
                    </span>
                    <span className="shrink-0 text-xs text-foreground-500">{formatCount(item.views)}</span>
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </aside>
      </div>

      <section className="mt-10 border-t border-background-200 pt-8">
        <h2 className="font-heading text-lg font-bold text-foreground-950">更多同分类视频</h2>
        <p className="mt-1 text-sm text-foreground-600">来自 {video.categoryName} 频道的其他内容</p>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
          {related.slice(0, 4).map((item) => (
            <VideoCard key={item.id} video={item} />
          ))}
        </div>
      </section>
    </div>
  );
}